import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomSheet } from '../base/BottomSheet';
import { getKycLabel, isEkycVerified } from '../../store';
import type { KycStatus } from '../../types';

interface AvatarMenuItem {
  id: string;
  label: string;
  icon: string;
  path?: string;
  actionType?: 'navigate' | 'bottomSheet' | 'logout';
  bottomSheetTitle?: string;
  bottomSheetContent?: string;
  isDanger?: boolean;
  kycInfo?: boolean;
}

interface AvatarMenuProps {
  open: boolean;
  onClose: () => void;
  user: {
    displayName: string;
    role: string;
    kycStatus: KycStatus;
    code?: string;
    creditScore?: number;
  };
  onLogout: () => void;
}

export function AvatarMenu({ open, onClose, user, onLogout }: AvatarMenuProps) {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [infoSheetOpen, setInfoSheetOpen] = useState(false);
  const [infoSheetTitle, setInfoSheetTitle] = useState('');
  const [infoSheetContent, setInfoSheetContent] = useState('');
  const [ekycSheetOpen, setEkycSheetOpen] = useState(false);
  const isHTX = user.role === 'Hợp tác xã';
  const ekycOk = isEkycVerified({ kycStatus: user.kycStatus });

  const items: AvatarMenuItem[] = isHTX
    ? [
        {
          id: 'account',
          label: 'Thông tin tài khoản',
          icon: 'ri-user-settings-line',
          path: '/htx/ho-so-htx',
        },
        {
          id: 'ekyc',
          label: 'Trạng thái eKYC',
          icon: 'ri-shield-check-line',
          actionType: 'bottomSheet',
          kycInfo: true,
        },
        {
          id: 'settings',
          label: 'Cài đặt',
          icon: 'ri-settings-3-line',
          path: '/htx/cai-dat',
        },
        {
          id: 'logout',
          label: 'Đăng xuất',
          icon: 'ri-logout-box-r-line',
          isDanger: true,
          actionType: 'logout',
        },
      ]
    : [
        {
          id: 'account',
          label: 'Thông tin tài khoản',
          icon: 'ri-user-settings-line',
          path: '/nong-ho/ho-so',
        },
        {
          id: 'ekyc',
          label: 'Trạng thái eKYC',
          icon: 'ri-shield-check-line',
          actionType: 'bottomSheet',
          kycInfo: true,
        },
        {
          id: 'settings',
          label: 'Cài đặt',
          icon: 'ri-settings-3-line',
          path: '/nong-ho/ho-so',
        },
        {
          id: 'logout',
          label: 'Đăng xuất',
          icon: 'ri-logout-box-r-line',
          isDanger: true,
          actionType: 'logout',
        },
      ];

  // Handle open/close with animation
  useEffect(() => {
    if (open) {
      setVisible(true);
      setClosing(false);
    } else if (visible) {
      setClosing(true);
      const timer = setTimeout(() => setVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [open, visible]);

  // ESC to close
  useEffect(() => {
    if (!visible || closing) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [visible, closing, onClose]);

  const handleItemClick = useCallback((item: AvatarMenuItem) => {
    if (item.actionType === 'logout') {
      onClose();
      setTimeout(() => onLogout(), 150);
      return;
    }

    if (item.actionType === 'bottomSheet') {
      if (item.kycInfo) {
        setEkycSheetOpen(true);
        return;
      }
      setInfoSheetTitle(item.bottomSheetTitle || '');
      setInfoSheetContent(item.bottomSheetContent || '');
      setInfoSheetOpen(true);
      return;
    }

    if (item.path) {
      onClose();
      setTimeout(() => navigate(item.path), 150);
    }
  }, [navigate, onClose, onLogout]);

  const kycBadge = (() => {
    const status = user.kycStatus || 'registered';
    if (status === 'ekyc_verified') {
      return { bg: 'bg-green-100', text: 'text-green-700', icon: 'ri-checkbox-circle-fill', iconColor: 'text-green-500' };
    }
    if (status === 'ekyc_pending') {
      return { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: 'ri-time-fill', iconColor: 'text-yellow-500' };
    }
    if (status === 'ekyc_need_more') {
      return { bg: 'bg-orange-100', text: 'text-orange-600', icon: 'ri-error-warning-fill', iconColor: 'text-orange-500' };
    }
    if (status === 'ekyc_failed') {
      return { bg: 'bg-red-100', text: 'text-red-600', icon: 'ri-close-circle-fill', iconColor: 'text-red-500' };
    }
    return { bg: 'bg-gray-100', text: 'text-gray-500', icon: 'ri-question-fill', iconColor: 'text-gray-400' };
  })();

  if (!visible) return null;

  return (
    <>
      {/* Full-viewport overlay — blocks interaction with content behind */}
      <div
        className={`fixed inset-0 z-[95] transition-opacity duration-200 ${
          closing ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ pointerEvents: closing ? 'none' : 'auto' }}
        onClick={onClose}
      />

      {/* Dropdown panel — fixed near top-right */}
      <div
        ref={menuRef}
        className={`fixed z-[96] w-72 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-200 ${
          closing
            ? 'opacity-0 scale-95 translate-y-1'
            : 'opacity-100 scale-100 translate-y-0'
        }`}
        style={{
          top: '56px',
          right: 'max(16px, calc((100vw - 430px) / 2 + 16px))',
          pointerEvents: closing ? 'none' : 'auto',
        }}
      >
        {/* User info header */}
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-agrida-100 flex items-center justify-center text-agrida-700 font-bold text-sm shrink-0">
              {user.displayName.charAt(0)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-800 truncate">{user.displayName}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-gray-400">{isHTX ? 'Hợp tác xã' : 'Nông hộ'}</span>
                {user.code && (
                  <span className="text-xs text-gray-300">· {user.code}</span>
                )}
              </div>
            </div>
          </div>
          <div className="mt-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${kycBadge.bg} ${kycBadge.text}`}>
              <i className={`${kycBadge.icon} text-xs ${kycBadge.iconColor}`} />
              {getKycLabel(user.kycStatus || 'registered')}
            </span>
            {user.creditScore && (
              <span className="ml-2 text-xs text-gray-400">Score: {user.creditScore}</span>
            )}
          </div>
        </div>

        {/* Menu items */}
        <div className="py-1">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleItemClick(item)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors min-h-[44px] ${
                item.isDanger
                  ? 'text-red-500 hover:bg-red-50 active:bg-red-100'
                  : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100'
              }`}
            >
              <span className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${item.isDanger ? 'bg-red-50' : 'bg-gray-100'}`}>
                <i className={`${item.icon} text-base ${item.isDanger ? 'text-red-400' : 'text-gray-500'}`} />
              </span>
              <span className="text-sm font-medium whitespace-nowrap">
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* eKYC Info Sheet */}
      <BottomSheet
        open={ekycSheetOpen}
        onClose={() => setEkycSheetOpen(false)}
        title="Trạng thái eKYC"
      >
        <div className="py-2">
          <div className="flex items-center justify-center mb-4">
            <span className={`w-16 h-16 rounded-full flex items-center justify-center ${ekycOk ? 'bg-green-50' : 'bg-orange-50'}`}>
              <i className={`text-3xl ${ekycOk ? 'ri-shield-check-line text-green-500' : 'ri-shield-user-line text-orange-500'}`} />
            </span>
          </div>
          <div className="text-center mb-3">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${kycBadge.bg} ${kycBadge.text}`}>
              <i className={`${kycBadge.icon} ${kycBadge.iconColor}`} />
              {getKycLabel(user.kycStatus || 'registered')}
            </span>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed text-center">
            {ekycOk
              ? 'Tài khoản của bạn đã được xác thực eKYC. Bạn có thể sử dụng đầy đủ các tính năng của Agrida, bao gồm vay vốn, xin hạn mức và nhận vật tư từ HTX.'
              : 'Bạn cần hoàn tất xác thực eKYC để sử dụng đầy đủ các tính năng của Agrida. eKYC giúp bảo vệ tài khoản và đảm bảo các giao dịch tài chính an toàn.'}
          </p>
          {!ekycOk && (
            <button
              type="button"
              onClick={() => {
                setEkycSheetOpen(false);
                onClose();
                setTimeout(() => navigate('/ekyc'), 200);
              }}
              className="w-full mt-4 py-2.5 bg-agrida-600 text-white rounded-xl text-sm font-medium hover:bg-agrida-700 transition-colors whitespace-nowrap"
            >
              Thực hiện eKYC ngay
            </button>
          )}
        </div>
      </BottomSheet>

      {/* General info sheet */}
      <BottomSheet
        open={infoSheetOpen}
        onClose={() => setInfoSheetOpen(false)}
        title={infoSheetTitle}
      >
        <div className="py-2">
          <div className="flex items-center justify-center mb-4">
            <span className="w-16 h-16 rounded-full bg-agrida-50 flex items-center justify-center">
              <i className="ri-information-line text-3xl text-agrida-500" />
            </span>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed text-center">{infoSheetContent}</p>
        </div>
      </BottomSheet>
    </>
  );
}